/**
 * PersonalDataBox Component
 * 
 * Main container component that manages different authentication views.
 * This component has been refactored to separate the different views into
 * dedicated components for better maintainability and code organization.
 * 
 * Views:
 * - LoggedInView: Shows when user is already authenticated
 * - CreateAccountView: Account creation form
 * - LoginView: User login form
 * - ErrorDisplay: Shared error display component
 */

import React, { useState, useEffect } from 'react';
import LoggedInView from './personal-data-views/LoggedInView';
import CreateAccountView from './personal-data-views/CreateAccountView';
import LoginView from './personal-data-views/LoginView';
import ErrorDisplay from './personal-data-views/ErrorDisplay';
import { registrationService, CreateAccountData } from '../../services/registrationService';

interface PersonalDataBoxProps {
  isLoggedIn: boolean;
  userName: string;
  userEmail: string;
  onLoginAttempt?: (email: string, password: string) => Promise<boolean>;
  onLogout?: () => Promise<void>;
  onSignInClick?: () => void;
  onSignOutClick?: () => void;
}

type ViewState = 'createAccount' | 'login' | 'loggedIn';

const PersonalDataBox: React.FC<PersonalDataBoxProps> = ({
  isLoggedIn,
  userName,
  userEmail,
  onLoginAttempt,
  onLogout,
  onSignInClick,
  onSignOutClick,
}) => {
  const [currentView, setCurrentView] = useState<ViewState>(isLoggedIn ? 'loggedIn' : 'login');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoggedIn) {
      setCurrentView('loggedIn');
    } else if (currentView === 'loggedIn') {
      setCurrentView('login');
    }
  }, [isLoggedIn, currentView, userName]);

  const handleLoginSubmit = async (email: string, password: string) => {
    setIsLoading(true);
    setLoginError(null);

    if (!email || !password) {
      setLoginError('Por favor, preencha email e senha');
      setIsLoading(false);
      return;
    }

    try {
      if (onLoginAttempt) {
        const success = await onLoginAttempt(email, password);
        if (!success) {
          setLoginError('Email ou senha incorretos');
        }
      } else if (onSignInClick) {
        onSignInClick();
      } else {
        console.error('Login function not provided');
        setLoginError('Não foi possível realizar o login');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Erro ao fazer login. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccountSubmit = async (data: CreateAccountData) => {
    setIsLoading(true);
    setLoginError(null);

    try {
      if (!data.name || !data.email || !data.phone || !data.password) {
        setLoginError('Por favor, preencha todos os campos.');
        return;
      }
      const result = await registrationService.createAccount(data);
      if (result.success) {
        if (onLoginAttempt) {
          const loginSuccess = await onLoginAttempt(data.email, data.password);
          if (!loginSuccess) {
            setCurrentView('login');
          }
        } else {
          setLoginError(null);
          setCurrentView('login');
        }
      } else {
        setLoginError(result.message || 'Erro ao criar conta. Tente novamente.');
      }
    } catch (error) {
      setLoginError('Erro ao criar conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchToLogin = () => {
    setCurrentView('login');
    setLoginError(null);
  };

  const handleSwitchToCreateAccount = () => {
    setCurrentView('createAccount');
    setLoginError(null);
  };

  const handleForgotPassword = () => {
    window.location.href = 'https://www.dio.me/forgot-password';
    setLoginError(null);
  };

  const renderHeader = () => (
    <div className="flex items-center gap-2 mb-4">
      <span className="font-semibold text-green-700">Identificação</span>
    </div>
  );

  const renderFooter = () => (
    <div className="mt-6 text-center">
      <div className="text-xs text-gray-500 mb-2">
        Ao clicar no botão "Continuar", você aceita nossos{' '}
        <a href="https://www.dio.me/terms" target="_blank" className="font-bold text-decoration-line: underline hover:text-gray-400">Termos de uso</a>{' '}
        e{' '}
        <a href="https://www.dio.me/policies" target="_blank" className="font-bold text-decoration-line: underline hover:text-gray-400">Políticas de Privacidade</a>
      </div>
      <div className="text-xs text-gray-500">
        Em caso de problemas, acesse o link do{' '}
        <a href="https://digitalinnovationone.atlassian.net/servicedesk/customer/portals" target="_blank" className="font-bold text-decoration-line: underline hover:text-gray-400">suporte</a>
      </div>
    </div>
  );

  if (isLoggedIn) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 flex flex-col gap-4 min-h-[400px]">
        {renderHeader()}
        <LoggedInView
          userName={userName}
          userEmail={userEmail}
          onLogout={onLogout}
          onSignOutClick={onSignOutClick}
          onSwitchToLogin={handleSwitchToLogin}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 flex flex-col gap-4 min-h-[400px]">
      {renderHeader()}

      {loginError && <ErrorDisplay error={loginError} />}

      {currentView === 'createAccount' ? (
        <CreateAccountView
          onSubmit={handleCreateAccountSubmit}
          onSwitchToLogin={handleSwitchToLogin}
          isLoading={isLoading}
        />
      ) : (
        <LoginView
          onSubmit={handleLoginSubmit}
          onSwitchToCreateAccount={handleSwitchToCreateAccount}
          onForgotPassword={handleForgotPassword}
          isLoading={isLoading}
        />
      )}

      {renderFooter()}
    </div>
  );
};

export default PersonalDataBox;